import { useState } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MENU_SURFACE } from '@/components/ui/menu-surface'
import { CategoryIcon } from '@/lib/categoryIcon'
import { ICON_CATALOG, type IconGroup } from '@/lib/iconCatalog'

export interface IconOption {
  name: string
  label: string
}

function matches(opt: IconOption, query: string): boolean {
  return opt.label.toLowerCase().includes(query) || opt.name.replace(/-/g, ' ').includes(query)
}

// A visual picker over a curated icon set — rather than a free-typed icon-name input,
// this never lets a user save a typo or an icon name that doesn't exist. `options` is the
// context's own shortlist (e.g. CATEGORY_ICON_OPTIONS, ACCOUNT_ICON_OPTIONS), shown first
// as "Suggested" — deliberately not defaulted, so every call site is explicit about which
// set it means. The whole ICON_CATALOG follows beneath it, searchable.
export function IconPicker({
  value,
  onChange,
  options,
}: {
  value: string
  onChange: (icon: string) => void
  options: IconOption[]
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const suggested = new Set(options.map((o) => o.name))
  const allGroups: IconGroup[] = [
    { group: 'Suggested', icons: options },
    ...ICON_CATALOG.map((g) => ({
      group: g.group,
      icons: g.icons.filter((i) => !suggested.has(i.name)),
    })),
  ]
  const q = query.trim().toLowerCase()
  const groups = allGroups
    .map((g) => (q ? { ...g, icons: g.icons.filter((i) => matches(i, q)) } : g))
    .filter((g) => g.icons.length > 0)
  const selected = allGroups.flatMap((g) => g.icons).find((o) => o.name === value)

  function close() {
    setOpen(false)
    setQuery('')
  }

  return (
    <div
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation()
          close()
        }
      }}
    >
      <Button
        type="button"
        variant="ghost"
        aria-label="Icon"
        onClick={() => (open ? close() : setOpen(true))}
        className="h-auto w-full items-center gap-2 rounded-lg border border-[var(--fl-border)] bg-[var(--fl-surface)] px-3 py-2.5 text-sm font-normal hover:bg-[var(--fl-surface)]"
      >
        {value && (
          <CategoryIcon
            icon={value}
            size={16}
            className="flex h-5 w-5 shrink-0 items-center justify-center text-[var(--fl-text-1)]"
          />
        )}
        <span className={value ? 'text-[var(--fl-text-1)]' : 'text-[var(--fl-text-2)]'}>
          {selected ? selected.label : value || 'Choose an icon'}
        </span>
        <ChevronDown size={16} className="ml-auto shrink-0 text-[var(--fl-text-2)]" />
      </Button>
      {open && (
        <div
          tabIndex={-1}
          className={`${MENU_SURFACE} absolute z-10 mt-1 w-80 max-w-[calc(100vw-2rem)] p-2 outline-none`}
        >
          <div className="relative mb-2">
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--fl-text-3)]"
            />
            <input
              type="search"
              aria-label="Search icons"
              placeholder="Search icons"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-md border border-[var(--fl-border)] bg-[var(--fl-surface)] py-1.5 pl-8 pr-2 text-sm text-[var(--fl-text-1)] placeholder:text-[var(--fl-text-3)] focus:border-[var(--fl-primary)] focus:outline-none"
            />
          </div>
          <div role="listbox" aria-label="Icon options" className="max-h-60 overflow-y-auto">
            {groups.length === 0 && (
              <p className="px-1 py-4 text-center text-sm text-[var(--fl-text-2)]">
                No icons match
              </p>
            )}
            {groups.map((g) => (
              <div key={g.group} className="mb-2 last:mb-0">
                <p className="px-1 pb-1 text-xs font-medium text-[var(--fl-text-2)]">{g.group}</p>
                <div className="grid grid-cols-7 gap-1">
                  {g.icons.map((opt) => (
                    <Button
                      key={opt.name}
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      role="option"
                      aria-label={opt.label}
                      aria-selected={opt.name === value}
                      title={opt.label}
                      onMouseDown={(e) => {
                        // Keeps focus where it is so the wrapper's blur doesn't close the
                        // menu before this selection lands.
                        e.preventDefault()
                        onChange(opt.name)
                        close()
                      }}
                      className={
                        opt.name === value
                          ? 'bg-[var(--fl-surface-hover)] text-[var(--fl-text-1)]'
                          : 'text-[var(--fl-text-2)]'
                      }
                    >
                      <CategoryIcon icon={opt.name} size={16} />
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
